import React, { useState } from 'react';
import styled from 'styled-components';
import { IconImage } from '@bynder/icons';
import { Button, ModalBase, Input, Flex, textStyles, Tooltip, token } from '@bynder/design-system';
import FileSelector from './FileSelector';
import { isValidUrl } from '../../../helpers/url';
import { track } from '../../../helpers/track';
import type { SimilarityFilter } from './types';
import { emitSimilaritySearchEvent } from '../NLSSearch/utils';

export function SearchByImageModal() {
	const [open, setOpen] = useState(false);
	const [imageURL, setImageURL] = useState('');
	const [isSearchingByLink, setIsSearchingByLink] = useState(false);
	const [isUploadingImage, setIsUploadingImage] = useState(false);
	const isInvalidInput = !isValidUrl(imageURL);

	function onClose() {
		setOpen(false);
		setImageURL('');
	}

	function file2DataUrl(file: File) {
		// returns dataurl of a selected file
		return new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				resolve(reader.result as string);
			};
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	}

	function resizeImage(imageDataURL: string) {
		return new Promise<string>((resolve, reject) => {
			const image = new Image();
			image.crossOrigin = 'Anonymous';
			image.src = imageDataURL;
			image.onload = () => {
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');

				const widthMultiplier = image.height > image.width ? image.width / image.height : 1;
				const heightMultiplier =
					image.width > image.height ? image.height / image.width : 1;

				canvas.width = 299 * widthMultiplier;
				canvas.height = 299 * heightMultiplier;

				if (ctx) {
					ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
				}

				resolve(canvas.toDataURL('image/png', 0.75));
			};
			image.onerror = reject;
		});
	}

	const handleUpload = async (file: File[]) => {
		setIsUploadingImage(true);

		try {
			const imageDataURL = await file2DataUrl(file[0]);

			const resizedImageDataURL = await resizeImage(imageDataURL);
			searchForSimilarAssets({
				similarToAssetIds: null,
				similarToImageData: resizedImageDataURL,
				similarToURL: null,
				similarToText: null,
			});
		} catch (err) {
			console.error(err);
		}
	};

	const searchForSimilarAssets = (similarityFilter: SimilarityFilter) => {
		const skipAddingToFilterbar = false;

		emitSimilaritySearchEvent(similarityFilter, skipAddingToFilterbar);

		setImageURL('');
		setOpen(false);
		setIsUploadingImage(false);
		setIsSearchingByLink(false);
		track('Search by Image Executed', {
			product_category: 'Spotlight',
			functional_area: 'Search',
			search_type: imageURL ? 'Image URL' : 'Uploaded File',
		});
	};

	const handleOnChange = (newValue: string) => {
		setImageURL(newValue);
	};

	const handleSearchingByImageLink = () => {
		setIsSearchingByLink(true);
		searchForSimilarAssets({
			similarToAssetIds: null,
			similarToImageData: null,
			similarToURL: imageURL,
			similarToText: null,
		});
	};

	const handleSearchingByImageLinkOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && !isInvalidInput) {
			handleSearchingByImageLink();
		}
	};

	return (
		<WrapperSpan>
			<Tooltip content="Search by image">
				<Button
					title=""
					variant="clean"
					icon={<StyledIconImage />}
					onClick={() => setOpen(true)}
					isPressed={open}
					isSmall
					data-testid="search-by-image-spotlight-button"
				/>
			</Tooltip>

			<ModalBase isOpen={open} onClose={onClose}>
				<ModalBase.Header title="Search by Image" />
				<ModalBase.Content>
					<Flex direction="column" alignItems="center" justifyContent="center" gap="2">
						<Flex justifyContent="space-between" alignItems="center">
							<Paragraph>
								Upload an image to search.
								<Label> We support JPG, JPEG, PNG, WEBP images.</Label>
							</Paragraph>

							<FileSelector onFilesSelected={handleUpload} contentTypes={['image/*']}>
								<Button
									title="Upload an image"
									variant="primary"
									isLoading={isUploadingImage}
								>
									Upload
								</Button>
							</FileSelector>
						</Flex>
						<CustomDivider>OR</CustomDivider>
						<Flex gap="4">
							<Input
								value={imageURL}
								onChange={(newValue) => handleOnChange(newValue)}
								onKeyDown={handleSearchingByImageLinkOnEnter}
								placeholder="Paste image link"
								type="url"
								isInvalid={!!(imageURL && isInvalidInput)}
							/>
							<Button
								title="search for an image by url"
								variant="primary"
								onClick={() => handleSearchingByImageLink()}
								isLoading={isSearchingByLink}
								isDisabled={!imageURL || isInvalidInput}
							>
								Search
							</Button>
						</Flex>
						<LinkToKnowledgeBase
							href="https://support.bynder.com/hc/en-us/articles/13812016590354-Search-by-Image"
							target="_blank"
						>
							How to copy and paste links
						</LinkToKnowledgeBase>
					</Flex>
				</ModalBase.Content>
			</ModalBase>
		</WrapperSpan>
	);
}

const WrapperSpan = styled.span`
	position: absolute;
	top: 50%;
	right: 0px;
	transform: translateY(-50%);
	padding-right: 4px;
`;

const LinkToKnowledgeBase = styled.a`
	${textStyles.paragraphS};

	color: ${token.blue400}!important;
	align-self: flex-start;
	margin-top: ${token.spacing2};
`;

const Paragraph = styled.p`
	${textStyles.paragraphM}
`;

const CustomDivider = styled.div`
	display: flex;
	align-items: center;
	margin: 8px 0;
	width: 100%;

	&:before,
	&:after {
		content: '';
		flex-grow: 1;
		background: #0310261a;
		height: 1px;
		font-size: 0;
		line-height: 0;
		margin: 0 8px;
	}
`;

const StyledIconImage = styled(IconImage)`
	color: ${token.gray600a};

	&: hover {
		color: ${token.gray800a};
	}
`;

const Label = styled.label`
	color: ${token.gray600a};
	${textStyles.uiTextS}
`;
